/**
 * PublishConfirmModal — the disclosure gate for the Phase-3 autopost path
 * (spec §5.7.3 · "Confirm publish").
 *
 * We render this even in Phase 1/2 because the Publish button only appears
 * when the variant's `autopostable` flag is true. If the tenant has flipped
 * the accounts toggle on, we still hit the API — which returns
 * `AUTOPOST_NOT_YET_AVAILABLE` (501) — and the caller shows a toast
 * explaining that Phase 3 is where autopost actually lands.
 */
import { AlertOctagon } from 'lucide-react';
import type { OffPageBriefVariant } from '@seo/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/spinner';
import { PLATFORM_LABELS } from '@/features/off-page-briefs/lib/variants';

export interface PublishConfirmModalProps {
  variant: OffPageBriefVariant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  loading: boolean;
}

export function PublishConfirmModal({
  variant,
  open,
  onOpenChange,
  onConfirm,
  loading,
}: PublishConfirmModalProps): JSX.Element {
  const platformLabel = PLATFORM_LABELS[variant.platform];
  const truncatedTitle = variant.title
    ? variant.title.length > 60
      ? `${variant.title.slice(0, 57)}…`
      : variant.title
    : `${platformLabel} draft`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Publish to {platformLabel}?</DialogTitle>
          <DialogDescription>
            Publish "{truncatedTitle}" to {platformLabel}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
          <AlertOctagon className="mt-0.5 h-4 w-4 flex-none text-destructive" />
          <div className="space-y-1 text-foreground/90">
            <p className="font-medium">This is public.</p>
            <p className="text-muted-foreground">
              If it violates {platformLabel}'s rules, {platformLabel} may remove
              it — we cannot undo that. To remove after publish, delete it in{' '}
              {platformLabel} directly.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={loading}>
            {loading ? (
              <>
                <Spinner /> Publishing…
              </>
            ) : (
              'Confirm publish'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
